// The single dockview `components` entry every panel in the workspace uses.
// dockview identifies *which* mode to render via `params.panelId` (looked up
// in PanelRegistryContext) rather than one registered component per mode -
// simpler than 14 near-identical registrations, and keeps dockview's own
// component registry decoupled from the app's mode list.
import { useEffect, useRef, type CSSProperties, type ReactNode } from 'react';
import type { IDockviewPanelProps } from 'dockview-react';
import { usePanelRegistry } from './PanelRegistryContext';
import { useDevicePixelSize } from './useDevicePixelSize';
import type { PanelId } from './types';

export interface PanelHostParams {
  panelId: PanelId;
}

// Matches the flex row every mode's Route already renders itself into today
// (ScreenEditor's `<div style={{ flex: 1, display: 'flex', minHeight: 0 }}>`
// around the old `{mode === 'x' && <XRoute/>}` switch) - the wrapper here
// takes over that role so a Route's own internals see an identical parent.
const contentStyle: CSSProperties = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  flexDirection: 'row',
  minHeight: 0,
  overflow: 'hidden',
};

/**
 * Canvas-owning panels (Edit, Map Builder, Characters, Animation, FX,
 * Cutscenes) get an inner container whose CSS box is frozen at the last
 * known-good size
 * (via useDevicePixelSize) instead of tracking the outer, dockview-owned
 * box live. When a tab goes inactive under dockview's default
 * `onlyWhenVisible` renderer, the outer box collapses to zero; freezing the
 * inner box means the Route's own `<Canvas>` - whose internals this task
 * must not touch - never observes that collapse as a resize.
 */
function StableSizeContent({ children }: { children: ReactNode }) {
  const outerRef = useRef<HTMLDivElement>(null);
  const stable = useDevicePixelSize(outerRef);
  return (
    <div ref={outerRef} style={contentStyle}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          minHeight: 0,
          overflow: 'hidden',
          width: stable ? stable.width : '100%',
          height: stable ? stable.height : '100%',
          flexShrink: 0,
        }}
      >
        {children}
      </div>
    </div>
  );
}

export function PanelHost(props: IDockviewPanelProps<PanelHostParams>) {
  const registry = usePanelRegistry();
  const descriptor = registry.get(props.params.panelId);

  if (!descriptor) {
    // Should be unreachable: normalizeLayout and Workspace's own
    // construction only ever add panels for ids present in the registry.
    // Fails loud rather than silently rendering nothing, since a descriptor
    // going missing means the registry and the persisted/constructed layout
    // have drifted apart.
    throw new Error(`panel-system: no PanelDescriptor registered for panel id "${props.params.panelId}".`);
  }

  // Fires when dockview truly removes this panel (e.g. a preset switch
  // evicts it) - not on a mere tab-hide, which keeps this component mounted
  // under dockview's default onlyWhenVisible renderer.
  useEffect(() => () => descriptor.dispose?.(), [descriptor]);

  const content = descriptor.canvasOwning ? (
    <StableSizeContent>{descriptor.render()}</StableSizeContent>
  ) : (
    <div style={contentStyle}>{descriptor.render()}</div>
  );

  return content;
}
